import React, { FC } from 'react';
import { AddressDisplay, GenExternalLink, GenNavTile } from '@/components';
import { TypeAccountNavTileFields } from '@/core/generated-types';
import { constructLegacyCustomerPortalLink } from '@/utils/shopSubdomain';
import {
  TypeAddressRecord,
  TypeUserRecord,
} from '@/lib/api/accountManager/types';
import { TypeSubscriptionRecord } from '@/lib/api/subscriptionManager/types';
import { TypeAccountSettingsPageTranslations } from '@/utils/translations/types/TypeAccountSettingsPageTranslations';
import interpolate from '@/utils/translations/interpolate';
import formatPossessiveProperNoun from '@/utils/translations/formatPossessiveProperNoun';

export type AddressBookTileProps = {
  t: TypeAccountSettingsPageTranslations;
  user: TypeUserRecord;
  subscriptions: TypeSubscriptionRecord[] | undefined;
  tile: TypeAccountNavTileFields;
  locale: string | undefined;
};

type AddressDisplayProps = {
  address: TypeAddressRecord;
};

const AddressBookTile: FC<AddressBookTileProps> = ({
  t,
  user,
  subscriptions,
  tile,
  locale,
}) => {
  if (user?.addresses?.length === 0) return null;
  if (!subscriptions || subscriptions.length === 0) {
    return null;
  }

  const anyDisplayableAddresses = user.addresses.some(_findChildForAddress);

  if (!anyDisplayableAddresses) return null;

  const Address: FC<AddressDisplayProps> = ({ address }) => {
    const linkedChild = _findChildForAddress(address);
    const editAddressLink = constructLegacyCustomerPortalLink(locale, [
      user.uuid,
      'account-settings',
      'edit-shipping-address',
      address.uuid,
    ]);

    // if we are not able to link this address to a subscription / child then it is not relevant to display
    if (!linkedChild) {
      return null;
    }

    return (
      <div className="mb-6 flex items-start justify-between last:mb-0">
        <div>
          <p className="font-bold">
            {interpolate(t.childsPlayKits, {
              childName: formatPossessiveProperNoun(linkedChild.name, locale),
            })}
          </p>
          <AddressDisplay address={address} />
        </div>
        <GenExternalLink href={editAddressLink} cta={t.editCta} />
      </div>
    );
  };

  function _findChildForAddress(address: TypeAddressRecord) {
    const linkedSubscription =
      subscriptions &&
      subscriptions.find(
        (subscription) => subscription.delivery_address_uuid === address.uuid,
      );
    const childUuid = linkedSubscription?.child_uuid;
    return user.children?.find((child) => child.uuid === childUuid);
  }

  return (
    <GenNavTile
      title={tile.title}
      subTitle={tile.subheader}
      description={tile.description}
    >
      {user.addresses.map((address) => (
        <Address address={address} key={address.uuid} />
      ))}
    </GenNavTile>
  );
};

export default React.memo(AddressBookTile);
